import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  alpha,
  Fade,
  Skeleton,
} from "@mui/material";
import CountUp from 'react-countup';
import {
  BusinessCenterOutlined,
  DateRangeOutlined,
  HowToRegOutlined,
  PersonOutlined,
  AssignmentTurnedInOutlined,
  TrendingUpOutlined,
  DashboardCustomize,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { appSelector } from "../../redux/selector";
import { getMyRecruitersAction } from "../../redux/action";
import { userSelector } from "../../user-redux/selector";
import axios from "axios";
import Key from "../../../clientVariables.json";
import { Link, useNavigate } from "react-router-dom";

const Admindashboardcards = () => {
  const navigate = useNavigate();
  const { myRecruiters } = useSelector(appSelector);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { user } = useSelector(userSelector);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State to hold counts
  const [counts, setCounts] = useState({
    recruiterCount: 0,
    activeCandidates: 0,
    jobsCount: 0,
    submissionsCount: 0,
    selectedCount: 0,
  });

  // Beautiful color palette with gradients
  const colors = {
    primary: {
      main: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      light: "#f093fb"
    },
    secondary: {
      main: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      light: "#ffecd2"
    },
    success: {
      main: "#4facfe",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      light: "#a8edea"
    },
    warning: {
      main: "#fd9644",
      gradient: "linear-gradient(135deg, #fd9644 0%, #fe4a49 100%)",
      light: "#fed6e3"
    },
    info: {
      main: "#a8edea",
      gradient: "linear-gradient(135deg, #628f8dff 0%, #e91d5eff 100%)",
      light: "#7b2964ff"
    },
    background: "#fafbfc",
    surface: "#ffffff",
    text: "#2d3748",
    textSecondary: "#718096",
    border: "#e2e8f0",
  };

  // Beautiful Icon Component with gradient background
  const BeautifulIcon = ({ children, gradient, size = 'medium' }) => {
    const sizeMap = {
      small: { container: 52, icon: 26 },
      medium: { container: 60, icon: 30 },
      large: { container: 68, icon: 34 }
    };

    const currentSize = sizeMap[size];

    return (
      <Box
        sx={{
          height: currentSize.container,
          width: currentSize.container,
          borderRadius: '16px',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `
            0 8px 25px ${alpha('#000', 0.1)},
            0 3px 10px ${alpha('#000', 0.05)}
          `,
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            transition: 'left 0.5s ease',
          },
          '&:hover': {
            transform: 'translateY(-3px) scale(1.05)',
            boxShadow: `
              0 15px 35px ${alpha('#000', 0.15)},
              0 5px 15px ${alpha('#000', 0.08)}
            `,
            '&::before': {
              left: '100%',
            }
          }
        }}
      >
        {React.cloneElement(children, {
          sx: {
            fontSize: currentSize.icon,
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
            transition: 'all 0.3s ease',
          }
        })}
      </Box>
    );
  };

  // Beautiful Card Component with modern design
  const BeautifulCard = ({
    redirect,
    title,
    subtitle,
    icon,
    count,
    gradient,
    isLoading = false
  }) => (
    <Fade in={!isLoading} timeout={800}>
      <Card
        sx={{
          p: { xs: 2, sm: 2.5, md: 2 },
          borderRadius: '20px',
          background: `
            linear-gradient(145deg, 
              ${colors.surface} 0%, 
              ${alpha('#f8fafc', 0.8)} 100%
            )
          `,
          border: `1px solid ${alpha(colors.border, 0.6)}`,
          boxShadow: `
            0 10px 40px ${alpha('#000', 0.08)},
            0 2px 8px ${alpha('#000', 0.04)},
            inset 0 1px 0 ${alpha('#fff', 0.9)}
          `,
          position: 'relative',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          cursor: redirect ? 'pointer' : 'default',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at top right, 
                ${alpha('#667eea', 0.02)} 0%, 
                transparent 70%
              )
            `,
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `
              0 20px 60px ${alpha('#000', 0.12)},
              0 8px 25px ${alpha('#000', 0.08)},
              inset 0 1px 0 ${alpha('#fff', 1)}
            `,
            borderColor: alpha('#667eea', 0.3),
            '& .icon-container': {
              transform: 'scale(1.1) rotate(5deg)',
            },
            '& .count-text': {
              transform: 'scale(1.05)',
            }
          }
        }}
        onClick={() => {
          if (!redirect) return;
          navigate(redirect)
        }}
      >
        {isLoading ? (
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {/* Icon Skeleton */}
              <Skeleton
                variant="rounded"
                width={isMobile ? 52 : 60}
                height={isMobile ? 52 : 60}
                sx={{
                  borderRadius: '16px',
                  background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.08)} 0%, ${alpha(colors.primary.main, 0.15)} 100%)`,
                  animation: 'shimmer 1.5s infinite linear',
                  '@keyframes shimmer': {
                    '0%': {
                      opacity: 0.5,
                    },
                    '50%': {
                      opacity: 1,
                    },
                    '100%': {
                      opacity: 0.5,
                    },
                  },
                }}
              />

              {/* Count Skeleton */}
              <Skeleton
                variant="rounded"
                width={90}
                height={isMobile ? 36 : 44}
                sx={{
                  borderRadius: '8px',
                  background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.08)} 0%, ${alpha(colors.primary.main, 0.15)} 100%)`,
                  animation: 'shimmer 1.5s infinite linear 0.2s',
                }}
              />
            </Stack>

            <Stack spacing={1}>
              {/* Title Skeleton */}
              <Skeleton
                variant="rounded"
                width="75%"
                height={isMobile ? 20 : 24}
                sx={{
                  borderRadius: '6px',
                  background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.08)} 0%, ${alpha(colors.primary.main, 0.15)} 100%)`,
                  animation: 'shimmer 1.5s infinite linear 0.4s',
                }}
              />

              {/* Subtitle Skeleton */}
              <Skeleton
                variant="rounded"
                width="60%"
                height={isMobile ? 16 : 18}
                sx={{
                  borderRadius: '6px',
                  background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.08)} 0%, ${alpha(colors.primary.main, 0.15)} 100%)`,
                  animation: 'shimmer 1.5s infinite linear 0.6s',
                }}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box className="icon-container" sx={{ transition: 'all 0.4s ease' }}>
                <BeautifulIcon
                  gradient={gradient}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {icon}
                </BeautifulIcon>
              </Box>

              <Typography
                className="count-text"
                variant="h3"
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: {
                    xs: '1.5rem',
                    sm: '1.75rem',
                    md: '2rem'
                  },
                  lineHeight: 1.1,
                  transition: 'all 0.3s ease',
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                <CountUp
                  end={count}
                  duration={2}
                  separator=","
                  decimals={0}
                  decimal="."
                  prefix=""
                  suffix=""
                  enableScrollSpy={true}
                  scrollSpyOnce={true}
                  delay={0.3}
                >
                  {({ countUpRef }) => (
                    <span ref={countUpRef} />
                  )}
                </CountUp>
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  color: colors.text,
                  lineHeight: 1.3,
                  letterSpacing: '-0.025em',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Card>
    </Fade>
  );

  const cardsData = [
    {
      title: "Total Recruiters",
      //subtitle: "Active hiring managers",
      icon: <HowToRegOutlined />,
      count: myRecruiters?.length || 0,
      gradient: colors.warning.gradient,
      redirect: '/admin-allocation'
    },
    {
      title: "Active Candidates",
      //subtitle: "Currently in pipeline",
      icon: <PersonOutlined />,
      count: counts.activeCandidates,
      gradient: colors.info.gradient,
      redirect: '/my-candidates'
    },
    {
      title: "Available Jobs",
      //subtitle: "Open positions",
      icon: <BusinessCenterOutlined />,
      count: counts.jobsCount,
      gradient: colors.primary.gradient,
      redirect: '/companies'
    },
    {
      title: "Total Submissions",
      //subtitle: "Applications received",
      icon: <DateRangeOutlined />,
      count: counts.submissionsCount,
      gradient: colors.secondary.gradient,
      redirect: null
    },
    {
      title: "Successfully Selected",
      //subtitle: "Hired candidates",
      icon: <AssignmentTurnedInOutlined />,
      count: counts.selectedCount,
      gradient: colors.success.gradient,
      redirect: null
    },
  ];

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        console.error("User ID is not available.");
        setError("User authentication required");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${Key.domain}/user/get_dashboard_count`, {
          user_id: user.id
        });

        const data = response?.data?.data?.[0] || {};

        setCounts({
          recruiterCount: data.recruiter_count || 0,
          activeCandidates: data.candidates_count || 0,
          jobsCount: data.jobs_count || 0,
          submissionsCount: data.candidate_submission_count || 0,
          selectedCount: data.submissions_selected_count || 0
        });
      } catch (error) {
        console.error("Error fetching dashboard counts:", error);
        setError("Failed to load dashboard data");
        setCounts({
          recruiterCount: 0,
          activeCandidates: 0,
          jobsCount: 0,
          submissionsCount: 0,
          selectedCount: 0
        });
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };

    const fetchRecruiters = () => {
      if (user?.companyid) {
        dispatch(getMyRecruitersAction({
          company_id: user.companyid
        }));
      }
    };

    fetchCounts();
    fetchRecruiters();

  }, [dispatch, user]);

  if (error && !loading) {
    return (
      <Box
        sx={{
          p: 1,
          textAlign: 'center',
          background: `
            linear-gradient(135deg, 
              ${alpha('#fee2e2', 0.3)} 0%, 
              ${colors.surface} 100%
            )
          `,
          borderRadius: 3,
          border: `1px solid ${alpha('#fca5a5', 0.3)}`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#dc2626' }}>
          Unable to Load Dashboard
        </Typography>
        <Typography variant="body2" color={colors.textSecondary}>
          {error}. Please refresh the page or try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 2 },
        background: `
          linear-gradient(135deg, 
            ${alpha('#667eea', 0.03)} 0%, 
            ${colors.background} 25%,
            ${alpha('#f093fb', 0.02)} 50%,
            ${colors.background} 75%,
            ${alpha('#4facfe', 0.03)} 100%
          )
        `,
        borderRadius: 3,
        position: 'relative',
        minHeight: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '#FFF',
          borderRadius: 3,
          pointerEvents: 'none',
        }
      }}
    >
      {/* Professional Heading */}
      {/* <DashboardHeading /> */}

      {/* Cards Grid */}
      <Grid container spacing={{ xs: 3, sm: 3.5, md: 4 }}>
        {cardsData.map((card, index) => (

          <Grid
            sx={{ padding: "0px" }}
            item
            xs={12}
            sm={6}
            md={4}
            lg={2.4}
            key={index}
          >
            <BeautifulCard
              redirect={card?.redirect}
              title={card.title}
              subtitle={card.subtitle}
              icon={card.icon}
              count={card.count}
              gradient={card.gradient}
              isLoading={loading}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Admindashboardcards;